import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import type { MenuActionItem } from 'src/interfaces/ui'
import type { WebhookSubscription } from 'src/interfaces/webhook'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  subscription: WebhookSubscription
  isSaving: boolean
  onEdit(subscription: WebhookSubscription): void
  onToggle(subscription: WebhookSubscription): void
}

const WebhookSubscriptionActionsCellComponent = ({
  subscription,
  isSaving,
  onEdit,
  onToggle,
}: Props) => {
  const items = useMemo<MenuActionItem[]>(() => {
    const toggleItem: MenuActionItem = subscription.isActive
      ? {
          label: 'Disable subscription',
          icon: <GSIcon iconName="gs_pause" size={20} css={tw`text-critical-500`} />,
          color: 'var(--mui-critical-500)',
          disabled: isSaving,
          dataTestId: `webhook-subscription-toggle-${subscription.id}`,
          onClick: () => {
            onToggle(subscription)
          },
        }
      : {
          label: 'Enable subscription',
          icon: <GSIcon iconName="gs_circle_check" size={20} css={tw`text-success-500`} />,
          disabled: isSaving,
          dataTestId: `webhook-subscription-toggle-${subscription.id}`,
          onClick: () => {
            onToggle(subscription)
          },
        }

    return [
      {
        label: 'Edit',
        icon: <GSIcon iconName="gs_pencil" size={20} css={tw`text-shades-1000`} />,
        dataTestId: `webhook-subscription-edit-${subscription.id}`,
        onClick: () => {
          onEdit(subscription)
        },
      },
      toggleItem,
    ]
  }, [subscription, isSaving, onEdit, onToggle])

  return (
    <RowActionsMenu
      items={items}
      ariaLabel="Webhook subscription actions"
      dataTestId={`webhook-subscription-actions-${subscription.id}`}
    />
  )
}

export const WebhookSubscriptionActionsCell = memo(WebhookSubscriptionActionsCellComponent)
